import { useEffect } from "react";
import {
  DatagridConfigurable,
  List,
  TextInput,
  TextField,
  useUnselectAll,
  TopToolbar,
  FilterButton,
  CreateButton,
  SelectColumnsButton,
  Pagination,
  FunctionField,
  NumberInput,
  useListContext,
  useRedirect,
  Button,
  type ListProps,
  type RaRecord,
} from "react-admin";
import SendIcon from "@mui/icons-material/Send";
import { formatINR } from "@/utils";
import { formatListDate } from "@/utils/date-format";
// import { FmbDataCsvImportButton } from "./csv-import/FmbDataCsvImportButton";
import { ExportButton } from "@/components/ExportButton";
import { FmbSabilMatchField, FmbItsDirectoryText } from "./common/FmbSabilMatchHint";
import { useIsFmbOutstandingBroadcastEnabled } from "@/hooks/useIsFmbOutstandingBroadcastEnabled";
import {
  buildFmbOutstandingBroadcastCreatePath,
  fmbDataListFiltersToOutstandingDeepLink,
} from "@/utils/fmb-outstanding-broadcast";

const RegistrationFilters = [
  <TextInput
    label="Search by Name, ITS, FMB, or File..."
    source="search"
    alwaysOn
    key={0}
    sx={{ minWidth: 300 }}
  />,
  <NumberInput
    label="Min outstanding %"
    source="minOutstandingPercent"
    key="minOutstandingPercent"
    min={0}
    max={100}
    step={1}
    helperText="Current takhmeen: pending > 0 and outstanding % ≥ this value (open households only)"
    parse={(v) => (v === "" || v == null ? undefined : Number(v))}
    format={(v) => (v == null || Number.isNaN(v as number) ? "" : v)}
  />,
];

function SendOutstandingReminderButton() {
  const { filterValues } = useListContext();
  const redirect = useRedirect();
  const enabled = useIsFmbOutstandingBroadcastEnabled();

  if (!enabled) {
    return null;
  }

  return (
    <Button
      label="Send outstanding reminder"
      onClick={() => {
        redirect(
          buildFmbOutstandingBroadcastCreatePath(
            fmbDataListFiltersToOutstandingDeepLink(filterValues as Record<string, unknown>)
          )
        );
      }}
    >
      <SendIcon />
    </Button>
  );
}

export default function FmbDataList(props: ListProps) {
  const { resource } = props;
  const unselectAll = useUnselectAll(resource);
  const PostBulkActionButtons = () => <div style={{ marginLeft: "25px" }} />;
  useEffect(() => {
    unselectAll();
  }, [resource, unselectAll]);

  const ListActions = () => (
    <TopToolbar sx={{ justifyContent: "start" }}>
      <SelectColumnsButton />
      <FilterButton />
      <CreateButton />
      <SendOutstandingReminderButton />
      {/* <FmbDataCsvImportButton /> */}
      <ExportButton />
    </TopToolbar>
  );
  return (
    <>
      <List
        {...props}
        sort={{ field: "updatedAt", order: "DESC" }}
        perPage={25}
        empty={false}
        pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
        filters={RegistrationFilters}
        actions={<ListActions />}
        sx={{
          "& .RaList-main form": {
            flex: "none",
          },
          "& .RaList-main .MuiToolbar-root": {
            justifyContent: "start",
          },
          "& .RaList-main .MuiTablePagination-spacer": {
            display: "none",
          },
        }}
      >
        <DatagridConfigurable
          size="small"
          sx={{
            color: "success.main",
          }}
          bulkActionButtons={<PostBulkActionButtons />}
          rowClick="show"
        >
          <TextField source="itsNo" label="ITS" key="itsNo" />
          <TextField source="name" label="Name" key="name" sortable />
          <FunctionField
            label="ITS in mohalla"
            key="itsDirectory"
            sortable={false}
            render={(record: RaRecord) => {
              const itsNo = typeof record.itsNo === "string" ? record.itsNo.trim() : "";
              if (!itsNo) return "—";
              return <FmbItsDirectoryText inDirectory={record.itsdata != null} />;
            }}
          />
          <FmbSabilMatchField label="Sabil" key="sabilMatch" sortable={false} />
          <FunctionField
            label="Pending Balance"
            key="pendingBalance"
            textAlign="right"
            sortBy="pendingBalance"
            render={(record: RaRecord) =>
              formatINR(
                (record.fmbTakhmeenCurrent as { pendingBalance?: unknown } | undefined)
                  ?.pendingBalance,
                { empty: "—" }
              )
            }
          />
          <FunctionField
            label="Paid Balance"
            key="paidBalance"
            textAlign="right"
            sortBy="paidBalance"
            render={(record: RaRecord) =>
              formatINR(
                (record.fmbTakhmeenCurrent as { paidBalance?: unknown } | undefined)?.paidBalance,
                { empty: "—" }
              )
            }
          />
          <FunctionField
            label="Takhmeen"
            key="takhmeenAmount"
            textAlign="right"
            sortBy="takhmeenAmount"
            render={(record: RaRecord) =>
              formatINR(
                (record.fmbTakhmeenCurrent as { takhmeenAmount?: unknown } | undefined)
                  ?.takhmeenAmount,
                { empty: "—" }
              )
            }
          />
          <TextField source="remarks" label="Remarks" key="remarks" />
          <FunctionField
            key="lastPaidDate"
            label="Last Paid Date"
            sortBy="lastPaidDate"
            render={(record: RaRecord) => formatListDate(record?.lastPaidDate, { empty: "—" })}
          />
        </DatagridConfigurable>
      </List>
    </>
  );
}
